import type { AppDispatch, RootState } from '../../store';
import { addElements, removeElements } from './layoutSlice';
import { addHistoryAction } from '../history/historySlice';
import { selectElements } from '../selection/selectionSlice';
import { generateId } from '../../utils/geometry';
import { generateRouteWithLlm } from '../../utils/dxfApi';
import { layoutElementFromEquipmentInstance } from '../../utils/stockerExport';
import { equipmentReviewStatus } from '../../utils/equipmentGeometry';
import {
  applyRouteEndpointRoles,
  isGeneratedRouteElement,
  normalizeGeneratedRouteHeights,
} from './loadGeneratedRouteThunk';
import { LayoutElement } from '../../types';

export interface LlmRouteResult {
  ok: boolean;
  message: string;
}

/**
 * Builds the route-generation INPUT from ONLY the equipment areas the user marked in this session
 * (`instanceIds` — the areas drawn between clicking the button and clicking finish). It does NOT
 * include the rest of the loaded layout, so a big floor plan doesn't balloon the LLM input. Each
 * marked area becomes a layer-1 equipment rectangle. Sends that to the backend, which archives the
 * input + generates + returns the route; this thunk then loads the generated track onto the canvas
 * (replacing any prior LLM track, one undo step). Async — resolves in a few to ~25s.
 */
export const generateRouteWithLlmThunk =
  (
    instanceIds?: string[],
    endpoints?: { start?: { x: number; y: number }; end?: { x: number; y: number } },
    model?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<LlmRouteResult> => {
    const state = getState();
    const allInstances = state.equipment.instances;
    // Scope to the marked-this-session areas; fall back to all marked areas if none were passed.
    const ids = instanceIds && instanceIds.length > 0 ? instanceIds : Object.keys(allInstances);
    const instanceElements = ids
      .map((id) => allInstances[id])
      .filter((instance) => instance && equipmentReviewStatus(instance) === 'accepted')
      .map(layoutElementFromEquipmentInstance)
      .filter((el): el is LayoutElement => el != null);

    if (instanceElements.length === 0) {
      return { ok: false, message: 'Mark at least one equipment area first, then click again.' };
    }

    // Input canvas = ONLY the marked areas (not the whole loaded layout).
    const elements: Record<string, LayoutElement> = {};
    const order: string[] = [];
    for (const el of instanceElements) {
      elements[el.id] = el;
      order.push(el.id);
    }
    const canvas = {
      kind: 'fab-project',
      elements,
      elementOrder: order,
      layers: state.layers.layers,
      layerOrder: state.layers.layerOrder,
    };

    const resp = await generateRouteWithLlm({
      canvas,
      model,
      startPoint: endpoints?.start ?? null,
      endPoint: endpoints?.end ?? null,
    });
    const newElements = normalizeGeneratedRouteHeights(
      Object.values(resp.route?.elements ?? {}) as LayoutElement[]
    );
    if (newElements.length === 0) {
      return { ok: true, message: resp.message ?? 'Route generated (no track returned to load).' };
    }

    // Replace any track a previous LLM run dropped (including its old START/END nodes), then load
    // the new track — one undo step.
    const previous = Object.values(state.layout.elements).filter(isGeneratedRouteElement);
    if (previous.length > 0) {
      dispatch({
        type: removeElements.type,
        payload: previous.map((e) => e.id),
        meta: { skipHistory: true },
      });
    }
    dispatch({ type: addElements.type, payload: newElements, meta: { skipHistory: true } });
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'replace',
        timestamp: Date.now(),
        elementIds: newElements.map((e) => e.id),
        previousState: previous as Partial<LayoutElement>[],
        newState: newElements as Partial<LayoutElement>[],
      })
    );
    dispatch(selectElements(newElements.map((e) => e.id)));
    applyRouteEndpointRoles(dispatch, resp.route);   // start -> Entry node, end -> Exit node

    const saved = resp.file ? ` (saved ${resp.file})` : '';
    return {
      ok: true,
      message: `Routed ${instanceElements.length} marked areas → ${newElements.length} track elements${saved}.`,
    };
  };
